export const APP_NAME = '1800D2C Admin Dashboard';

/**
 * The email domains that may sign in to this console, for display only.
 *
 * The API is the authority — `INTERNAL_EMAIL_DOMAINS` in
 * `apps/api/src/common/constants/index.ts` — and it is what actually refuses an
 * address; every refusal message the user sees comes back from there. This copy
 * exists because the login page and the Internal Users page both say the list
 * out loud before anyone has tried to sign in, and there is no request to hang
 * that on. Keep it in step with the API's, and add a domain in both places.
 */
export const INTERNAL_EMAIL_DOMAINS = ['klaystudio.co', 'klaymedia.com', '1800dtc.com'] as const;

/** "@a, @b and @c" — the list as a sentence. */
export function internalEmailDomainList(): string {
  const at = INTERNAL_EMAIL_DOMAINS.map((domain) => `@${domain}`);
  return at.length <= 1 ? (at[0] ?? '') : `${at.slice(0, -1).join(', ')} and ${at[at.length - 1]}`;
}

/**
 * A feature card's title and description — tool "Key Features" and agency
 * "What Sets Us Apart", for display and client-side validation only.
 *
 * The API is the authority (`FEATURE_CARD_*_MAX_LENGTH` in
 * `apps/api/src/common/constants/validation-limits.ts`) and it refuses anything
 * over these on both categories; this copy exists so the two forms can show a
 * counter and a message before the request goes out. Keep them in step.
 *
 * The site used to enforce the length by clipping the card's copy at render
 * with an ellipsis, which meant an admin could save copy nobody would ever
 * read in full. The card draws what it is given now.
 */
export const FEATURE_CARD_TITLE_MAX_LENGTH = 20;
export const FEATURE_CARD_DESCRIPTION_MAX_LENGTH = 70;

/** For display only; the API's `MIN_PUBLIC_BRAND_TOOLS` is what refuses a save. Keep them in step. */
export const MIN_PUBLIC_BRAND_TOOLS = 3;

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  CUSTOMERS: '/dashboard/customers',
  USERS: '/dashboard/users',
  BRANDS: '/dashboard/brands',
  BRAND_CATEGORIES: '/dashboard/brands/categories',
  BRAND_TAGS: '/dashboard/brands/tags',
  BRAND_LOCATIONS: '/dashboard/brands/locations',
  AGENCIES: '/dashboard/agencies',
  AGENCY_CATEGORIES: '/dashboard/agencies/categories',
  EXPERTS: '/dashboard/experts',
  TOOLS: '/dashboard/tools',
  TOOL_CATEGORIES: '/dashboard/tools/categories',
  TOOL_TAGS: '/dashboard/tools/tags',
  TOOL_FAQS: '/dashboard/tools/faqs',
  CONNECTION_REQUESTS: '/dashboard/connection-requests',
  TOOL_CLICKS: '/dashboard/tool-clicks',
  SETTINGS: '/dashboard/settings',
} as const;
